import { Component, OnInit, ElementRef, OnDestroy, inject } from '@angular/core';

import { Store, select } from '@ngrx/store';
import {
  Observable,
  interval,
  combineLatest,
  Subject,
  map,
  take,
  tap,
  first,
  takeUntil,
  distinctUntilChanged,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonTheme,
  ButtonType,
  NotificationTheme,
  NotificationLayout,
  LoaderTheme,
  IconPosition,
} from '@fcom/ui-components';
import { RootPaths } from '@fcom/core';
import { sortBy, compareAsJson, serializeParams } from '@fcom/core/utils';
import { Profile } from '@fcom/core-api/login';
import { profile } from '@fcom/core/selectors';

import { TripsActions } from '../../store/actions';
import { State, TripsState, FetchTripsStatus } from '../../interfaces/store.interface';
import { selectTripsState } from '../../store/selectors';
import { Trip, OutboundJourney } from '../../interfaces/pax-trip.interface';

enum Status {
  INITIAL,
  LOADING,
  COMPLETE,
  EMPTY,
  ERROR,
}

@Component({
  selector: 'fin-loyalty-upcoming-trips',
  templateUrl: './loyalty-upcoming-trips.component.html',
  styleUrls: ['./loyalty-upcoming-trips.component.scss'],
  standalone: false,
})
export class LoyaltyUpcomingTripsComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<State>>(Store);
  private languageService = inject(LanguageService);
  private element = inject(ElementRef);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly Status = Status;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  trips$: Observable<OutboundJourney[]>;
  visibleRows = 15;
  lastVisibleIndex: number;
  status: Status;
  tripState$: Observable<TripsState>;

  private profile$: Observable<Profile>;
  private memberNumber$: Observable<string>;
  private ngDestroyed$ = new Subject();

  constructor() {
    this.profile$ = this.store$.pipe(profile(), first());
    this.memberNumber$ = this.profile$.pipe(map((memberProfile: Profile) => memberProfile.memberNumber));
    this.tripState$ = this.store$.pipe(select(selectTripsState));
  }

  ngOnInit(): void {
    this.lastVisibleIndex = this.visibleRows - 1;
    this.store$.dispatch(TripsActions.fetchTrips());
    this.trips$ = this.getTrips();
    this.status = Status.INITIAL;
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }

  onRevealRows(): void {
    this.lastVisibleIndex = this.lastVisibleIndex + this.visibleRows;
    this.revealRows();
  }

  private getTrips(): Observable<OutboundJourney[]> {
    this.status = Status.LOADING;
    return combineLatest([this.tripState$, this.memberNumber$]).pipe(
      map(([tripState]) => {
        if (tripState.status === FetchTripsStatus.ERROR) {
          this.status = Status.ERROR;
        } else if (tripState.status === FetchTripsStatus.OK) {
          this.status = Status.COMPLETE;
          return sortBy(this.parseOutboundJourneys(tripState.trips), (trip) => trip.scheduledDateTimeUnix) || [];
        }
        return [];
      }),
      tap((trips: OutboundJourney[]) => {
        if (this.status === Status.COMPLETE && trips.length > 0) {
          this.revealRows();
        } else if (this.status !== Status.ERROR && this.status !== Status.LOADING) {
          this.status = Status.EMPTY;
        }
      }),
      distinctUntilChanged((a: OutboundJourney[], b: OutboundJourney[]): boolean => this.checkTripDistinction(a, b))
    );
  }

  private parseOutboundJourneys(trips: Trip[]): OutboundJourney[] {
    return trips.map((trip: Trip): OutboundJourney => {
      const outboundJourney = trip.journeys[0];
      return {
        departure: outboundJourney.departure.municipality,
        arrival: outboundJourney.arrival.municipality,
        scheduledDateTime: outboundJourney.departure.scheduledDateTime,
        scheduledDateTimeUnix: new Date(outboundJourney.departure.scheduledDateTime).getTime(),
        passengerAmount: outboundJourney.passengerAmount,
        bookingRef: trip.recLoc,
        mmbLink: this.generateMmbLink(trip.lastName, trip.recLoc),
      };
    });
  }

  private generateMmbLink(lastName: string, bookingRef: string): string {
    const bookingData = {
      lastName: lastName,
      recLoc: bookingRef,
    };

    return `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}?${serializeParams(bookingData)}`;
  }

  private revealRows(): void {
    setTimeout(() => {
      const rowElements = this.element.nativeElement.querySelectorAll('.fade-up:not(.fade-up--animate)');
      interval(150)
        .pipe(
          take(rowElements.length),
          map((index) => rowElements[index]),
          takeUntil(this.ngDestroyed$)
        )
        .subscribe((value) => {
          value.classList.add('fade-up--animate');
        });
    }, 0);
  }

  private checkTripDistinction(a: OutboundJourney[], b: OutboundJourney[]): boolean {
    return compareAsJson(
      a.map((trip: OutboundJourney) => trip.bookingRef).sort(),
      b.map((trip: OutboundJourney) => trip.bookingRef).sort()
    );
  }
}
